import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useApi } from '@getvim/internal-vim-os-sdk/react';
import { analyticsManager, PriorAuthAnalyticsManager } from './analyticsManager';
import { AnalyticsClient, PriorAuthAnalyticsEvents } from './types';
import { CurrentContext } from '../types/currentContext';
import { Tab } from '../types';

interface AnalyticsContextValue {
  analyticsClient: AnalyticsClient;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { trackAnalytics } = useApi();
  const [analyticsReady, setAnalyticsReady] = useState(false);

  useEffect(() => {
    if (!trackAnalytics) {
      return;
    }

    PriorAuthAnalyticsManager.setAnalyticsClient({ trackAnalytics });
    setAnalyticsReady(true);
  }, [trackAnalytics]);

  const analyticsClient = useMemo<AnalyticsClient>(
    () => ({
      track: <T extends keyof PriorAuthAnalyticsEvents>(
        event: T,
        properties: PriorAuthAnalyticsEvents[T],
      ) => analyticsManager.track(event, properties),
      setContext: (context: CurrentContext, tabName: Tab) =>
        analyticsManager.setContext(context, tabName),
      trackWithContext: <T extends keyof PriorAuthAnalyticsEvents>(
        event: T,
        properties: Partial<PriorAuthAnalyticsEvents[T]>,
      ) => analyticsManager.trackWithContext(event, properties),
    }),
    [],
  );

  const value = useMemo(() => ({ analyticsClient }), [analyticsClient]);

  if (!analyticsReady) {
    return null;
  }

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};
